/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { JSX } from 'react';
import './index.css';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import {
  $getRoot,
  $isTextNode,
  DOMConversionMap,
  DOMExportOutput,
  DOMExportOutputMap,
  EditorState,
  Klass,
  LexicalEditor,
  LexicalNode,
  ParagraphNode,
  TextNode,
} from 'lexical';

import { SharedHistoryContext } from './context/SharedHistoryContext';
import { ToolbarContext } from './context/ToolbarContext';

import Editor from './Editor';
import PlaygroundNodes from './nodes/PlaygroundNodes';
import { TableContext } from './plugins/TablePlugin';
import PlaygroundEditorTheme from './themes/PlaygroundEditorTheme';
import CasLexicalInputNumberPlugin from '../../plugins/CasLexicalInputNumberPlugin';
import { CasNumberBoxDialogHandlerPlugin } from '../../plugins/CasNumberBoxDialogHandlerPlugin';
import { $createImageNode } from './nodes/ImageNode';
import { FormLabel } from '@mui/material';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { $createCasDivNode, CasDivNode } from '../../CasNodes/CasDivNode';
import { CasLexicalInputNumberNode } from '../../CasLexicalNumber/CasLexicalInputNumberNode';

function prepareHtmlForEmail(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Add inline style to all <li> elements
  doc.querySelectorAll('li').forEach((li) => {
    const existing = li.getAttribute('style') || '';
    li.setAttribute(
      'style',
      `${existing}${existing && !existing.endsWith(';') ? ';' : ''}margin:0;`
    );
  });

  const PRE_WRAP_STYLES = ['white-space: pre-wrap', 'white-space:pre-wrap'];
  const elementsToClean = doc.querySelectorAll('[style*="white-space: pre-wrap"]');

  elementsToClean.forEach((element) => {
    const hasDoubleSpace = /\s\s/.test(element.textContent || '');

    if (!hasDoubleSpace) {
      const style = element.getAttribute('style') || '';
      const newStyle = style
        .split(';')
        .map((s) => s.trim())
        .filter((s) => s && !PRE_WRAP_STYLES.includes(s))
        .join('; ');

      if (newStyle) element.setAttribute('style', newStyle);
      else element.removeAttribute('style');
    }
  });

  // Clean up Redundant/Style-less Tags (span and strong) ---
  const unwrapIfStyleless = (tagName: string) => {
    doc.querySelectorAll(tagName).forEach((element) => {
      const style = element.getAttribute('style') || '';

      if (!style.trim() && element.attributes.length === 0) {
        const parent = element.parentNode;
        if (parent) {
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          parent.removeChild(element);
        }
      }
    });
  };

  unwrapIfStyleless('span');
  unwrapIfStyleless('strong');

  return doc.body.innerHTML;
}

export function htmlToEditorState(editor: LexicalEditor, htmlString: string) {
  editor.update(() => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(htmlString, 'text/html');
    const nodes = $generateNodesFromDOM(editor, dom);
    const root = $getRoot();
    root.clear();

    // Ensure only element/decorator nodes are appended
    nodes.forEach((node) => {
      if (node.isInline() || node.getType() === 'text') {
        // Wrap text or inline nodes in a paragraph
        const divBlock = $createCasDivNode();
        divBlock.append(node);
        root.append(divBlock);
      } else {
        root.append(node);
      }
    });
  });
}

const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [TextNode, (editor, target) => target.exportDOM(editor)],
  [TextNode, (editor, target) => target.exportDOM(editor)],
]);

const constructImportMap = (): DOMConversionMap => {
  const importMap: DOMConversionMap = {};

  // Wrap all TextNode importers with a function that also imports
  // the custom styles implemented by the playground
  Object.entries(TextNode.importDOM() || {}).forEach(([tag, fn]) => {
    importMap[tag] = (importNode) => {
      const importer = fn(importNode);
      if (!importer) {
        return null;
      }
      return {
        ...importer,
        conversion: (element) => {
          const output = importer.conversion(element);
          if (
            output === null ||
            output.forChild === undefined ||
            output.after !== undefined ||
            output.node !== null
          ) {
            return output;
          }

          const extraStyles = element.getAttribute('style') || '';
          if (extraStyles) {
            const { forChild } = output;
            return {
              ...output,
              forChild: (child, parent) => {
                const textNode = forChild(child, parent);
                if ($isTextNode(textNode)) {
                  textNode.setStyle(textNode.getStyle() + extraStyles);
                }
                return textNode;
              },
            };
          }
          return output;
        },
      };
    };
  });

  importMap['img'] = (node) => {
    return {
      conversion: () => {
        const src = node.getAttribute('src') || '';
        const altText = node.getAttribute('alt') || '';
        const imageNode = $createImageNode({ src, altText, captionsEnabled: false });
        return { node: imageNode };
      },
    };
  };
  return importMap;
};

type Props = {
  label?: string;
  placeholder?: string;
  isError?: boolean;
  initialValue?: string;
  onChange?: (value: string) => void;
};

export default function CasLexicalRichTextEditor({
  label,
  placeholder = 'Enter some rich text...',
  isError = false,
  initialValue,
  onChange,
}: Props): JSX.Element {
  const initialConfig = {
    namespace: 'Lexical Rich Text editor',
    theme: PlaygroundEditorTheme,
    nodes: [
      ...PlaygroundNodes,
      
      // Cas Nodes
      CasLexicalInputNumberNode,
      CasDivNode,
      {
        replace: ParagraphNode,
        with: () => $createCasDivNode(),
      },
    ],
    onError: (error: Error) => {
      throw error;
    },
    html: {
      export: exportMap,
      import: constructImportMap(),
    },
    editorState: (editor: LexicalEditor) => {
      if (initialValue) {
        htmlToEditorState(editor, initialValue);
      }
    },
  };

  const handleEditorChange = (_: EditorState, editor: LexicalEditor) => {
    editor.update(() => {
      const html = $generateHtmlFromNodes(editor);
      const cleanedHtml = prepareHtmlForEmail(html);

      const plainText = cleanedHtml.replace(/<[^>]+>/g, '').trim();
      onChange?.(plainText ? cleanedHtml : '');
    });
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`editor-shell ${isError ? 'editor-error' : ''}`}>
        {label && <FormLabel>{label}</FormLabel>}
        <SharedHistoryContext>
          <TableContext>
            <ToolbarContext>
              <Editor placeholder={placeholder} />
              <OnChangePlugin onChange={handleEditorChange} />

              {/* Cas Plugins */}
              <CasLexicalInputNumberPlugin />
              <CasNumberBoxDialogHandlerPlugin />
            </ToolbarContext>
          </TableContext>
        </SharedHistoryContext>
      </div>
    </LexicalComposer>
  );
}
